<template>
    <div class="sip-home">
        <section class="sip-page-head">
            <div>
                <span class="sip-eyebrow">Dashboard</span>
                <h1>{{ trans('general.home') }}</h1>
                <p>
                    Monitor tenant health, support queries, subscribers, and recent operations from one balanced admin workspace.
                </p>
            </div>
            <div class="sip-page-actions">
                <router-link v-if="hasPermission('list-query')" to="/query" class="sip-btn">{{ trans('query.query') }}</router-link>
                <router-link v-if="hasPermission('list-instance')" to="/instance" class="sip-btn sip-btn--primary">{{ trans('instance.instance') }}</router-link>
            </div>
        </section>

        <div v-if="!hasPermission('list-instance')" class="sip-surface-card sip-empty-state">
            <p class="mb-0">{{ trans('general.no_result_found') }}</p>
        </div>

        <template v-else>
            <section class="sip-metric-grid" aria-label="Instance status summary">
                <article v-for="item in snapshotCards" :key="item.key" class="sip-surface-card sip-metric-card" :class="`sip-metric-card--${item.tone}`">
                    <span>{{ item.label }}</span>
                    <strong>{{ item.value }}</strong>
                    <small>{{ item.caption }}</small>
                </article>
            </section>

            <section class="sip-content-grid">
                <article class="sip-surface-card sip-table-panel">
                    <div class="sip-section-head sip-section-head--with-actions">
                        <div>
                            <span>Instance status</span>
                            <h2>{{ trans('instance.instance') }}</h2>
                        </div>
                        <router-link to="/instance" class="sip-btn">{{ trans('instance.instance') }}</router-link>
                    </div>

                    <div class="sip-table-wrap">
                        <table class="sip-data-table">
                            <thead>
                                <tr>
                                    <th>Segment</th>
                                    <th>Status</th>
                                    <th>Count</th>
                                    <th>Posture</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in instanceRows" :key="row.key">
                                    <td>
                                        <strong>{{ row.segment }}</strong>
                                        <span>{{ row.caption }}</span>
                                    </td>
                                    <td><span class="sip-pill" :class="`sip-pill--${row.tone}`">{{ row.status }}</span></td>
                                    <td>{{ row.count }}</td>
                                    <td>{{ row.posture }}</td>
                                    <td><span class="sip-action-note" :class="{ 'sip-action-note--muted': row.muted }">{{ row.action }}</span></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </article>

                <aside class="sip-support-column">
                    <article v-if="hasPermission('list-query')" class="sip-surface-card sip-support-panel">
                        <div class="sip-section-head sip-section-head--compact">
                            <div>
                                <span>Support queue</span>
                                <h2>{{ trans('query.query') }}</h2>
                            </div>
                            <strong class="sip-resolved">{{ query_stats.resolved }} resolved</strong>
                        </div>
                        <div class="sip-support-card sip-support-card--summary">
                            <div>
                                <span class="sip-pill" :class="queryHealthTone">{{ queryHealthLabel }}</span>
                                <strong>{{ queryHealthTitle }}</strong>
                                <p>{{ queryHealthCopy }}</p>
                            </div>
                            <router-link to="/query" class="sip-btn">{{ trans('query.query') }}</router-link>
                        </div>
                    </article>

                    <article v-if="hasPermission('list-subscriber')" class="sip-surface-card sip-subscriber-card">
                        <span>Subscriber state</span>
                        <div class="sip-subscriber-line">
                            <strong>{{ subscriber_stats.subscribed }}</strong>
                            <p>{{ trans('subscriber.subscribed') }}</p>
                        </div>
                        <div class="sip-subscriber-line sip-subscriber-line--muted">
                            <strong>{{ subscriber_stats.unsubscribed }}</strong>
                            <p>{{ trans('subscriber.unsubscribed') }}</p>
                        </div>
                    </article>

                    <article class="sip-surface-card sip-chart-card">
                        <div class="sip-section-head sip-section-head--compact">
                            <div>
                                <span>Distribution</span>
                                <h2>{{ trans('instance.instance') }} status</h2>
                            </div>
                        </div>
                        <div class="sip-chart-wrap" v-show="graph.instance_status.labels && graph.instance_status.labels.length">
                            <doughnut-graph :graph="graph.instance_status"></doughnut-graph>
                        </div>
                        <p class="sip-empty-inline" v-if="!graph.instance_status.labels || !graph.instance_status.labels.length">
                            {{ trans('general.no_result_found') }}
                        </p>
                    </article>
                </aside>
            </section>

            <section class="sip-bottom-grid">
                <article class="sip-surface-card sip-activity-panel">
                    <div class="sip-section-head sip-section-head--compact">
                        <div>
                            <span>Activity</span>
                            <h2>{{ trans('activity.activity_log') }}</h2>
                        </div>
                    </div>
                    <ol v-if="activity_logs.length" class="sip-timeline">
                        <li v-for="activity_log in activity_logs.slice(0, 5)" :key="activity_log.id">
                            <span aria-hidden="true"></span>
                            <div>
                                <strong>{{ activityTitle(activity_log) }}</strong>
                                <p>{{ activityUserName(activity_log) }} · {{ activity_log.created_at | momentDateTime }}</p>
                            </div>
                        </li>
                    </ol>
                    <p class="sip-empty-inline" v-else>{{ trans('general.no_result_found') }}</p>
                </article>

                <article class="sip-surface-card sip-todo-panel">
                    <div class="sip-section-head sip-section-head--compact">
                        <div>
                            <span>Todos</span>
                            <h2>{{ trans('todo.todo') }}</h2>
                        </div>
                    </div>
                    <div v-if="todos.length" class="sip-todo-list">
                        <label v-for="todo in todos.slice(0, 5)" :key="todo.id" class="sip-todo">
                            <input type="checkbox" :checked="Boolean(todo.status)" disabled>
                            <span>
                                <strong>{{ todo.title }}</strong>
                                <small>{{ todo.date | moment }}</small>
                            </span>
                        </label>
                    </div>
                    <p class="sip-empty-inline" v-else>{{ trans('general.no_result_found') }}</p>
                </article>
            </section>
        </template>
    </div>
</template>

<script>
    import doughnutGraph from '../graph/doughnut-graph'

    const emptyInstance = () => ({
        total: 0,
        running: 0,
        expired: 0,
        suspended: 0,
        terminated: 0,
        trial: 0,
        pending: 0
    })

    const emptyQuery = () => ({
        total: 0,
        replied: 0,
        unanswered: 0,
        under_process: 0,
        awaiting_response: 0,
        resolved: 0
    })

    const emptySubscriber = () => ({
        subscribed: 0,
        unsubscribed: 0
    })

    export default {
        components: { doughnutGraph },
        mounted(){
            axios.get('/api/dashboard')
                .then(response => response.data)
                .then(response => {
                    this.instance_stats = response.instance_stats || emptyInstance();
                    this.query_stats = response.query_stats || emptyQuery();
                    this.subscriber_stats = response.subscriber_stats || emptySubscriber();
                    this.graph = response.graph || { instance_status: { labels: [], datasets: [] } };
                    this.activity_logs = response.activity_logs || [];
                    this.todos = response.todos || [];
                })
                .catch(error => {
                    helper.showDataErrorMsg(error);
                })
        },
        methods: {
            hasRole(role){
                return helper.hasRole(role);
            },
            hasPermission(permission){
                return helper.hasPermission(permission);
            },
            activityTitle(activityLog) {
                return this.trans('activity.' + activityLog.activity, {
                    activity: this.trans(activityLog.module + '.' + activityLog.module)
                });
            },
            activityUserName(activityLog) {
                const user = activityLog.user || {};
                const profile = user.profile || {};
                const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
                return name || this.trans('user.user');
            }
        },
        computed: {
            snapshotCards() {
                return [
                    {
                        key: 'total',
                        label: `${this.trans('general.total')} ${this.trans('instance.instance')}`,
                        value: this.instance_stats.total,
                        caption: 'All tenant environments',
                        tone: 'neutral',
                    },
                    {
                        key: 'running',
                        label: this.trans('instance.running'),
                        value: this.instance_stats.running,
                        caption: 'Currently active',
                        tone: 'good',
                    },
                    {
                        key: 'expired',
                        label: this.trans('instance.expired'),
                        value: this.instance_stats.expired,
                        caption: 'Renewal follow-up',
                        tone: Number(this.instance_stats.expired) ? 'warn' : 'neutral',
                    },
                    {
                        key: 'trial',
                        label: this.trans('instance.trial'),
                        value: this.instance_stats.trial,
                        caption: 'Conversion pipeline',
                        tone: 'info',
                    },
                ];
            },
            instanceRows() {
                return [
                    {
                        key: 'running',
                        segment: 'Active tenants',
                        caption: 'Production environments',
                        status: this.trans('instance.running'),
                        count: this.instance_stats.running,
                        posture: 'Healthy capacity',
                        action: 'Monitor',
                        tone: 'good',
                    },
                    {
                        key: 'expired',
                        segment: 'Renewal queue',
                        caption: 'Expired subscriptions',
                        status: this.trans('instance.expired'),
                        count: this.instance_stats.expired,
                        posture: Number(this.instance_stats.expired) ? 'Needs review' : 'Clear',
                        action: Number(this.instance_stats.expired) ? 'Renewal follow-up' : 'No action',
                        tone: 'warn',
                        muted: !Number(this.instance_stats.expired),
                    },
                    {
                        key: 'trial',
                        segment: 'Trial pipeline',
                        caption: 'Evaluation workspace',
                        status: this.trans('instance.trial'),
                        count: this.instance_stats.trial,
                        posture: Number(this.instance_stats.trial) ? 'Conversion watch' : 'No active trials',
                        action: Number(this.instance_stats.trial) ? 'Schedule check-in' : 'No action',
                        tone: 'info',
                        muted: !Number(this.instance_stats.trial),
                    },
                    {
                        key: 'risk',
                        segment: 'Risk holds',
                        caption: 'Suspended or terminated',
                        status: 'Clear',
                        count: Number(this.instance_stats.suspended) + Number(this.instance_stats.terminated),
                        posture: Number(this.instance_stats.suspended) || Number(this.instance_stats.terminated) ? 'Needs attention' : 'No active risk',
                        action: Number(this.instance_stats.suspended) || Number(this.instance_stats.terminated) ? 'Review holds' : 'No action',
                        tone: Number(this.instance_stats.suspended) || Number(this.instance_stats.terminated) ? 'warn' : 'quiet',
                        muted: !(Number(this.instance_stats.suspended) || Number(this.instance_stats.terminated)),
                    },
                ];
            },
            queryHealthTone() {
                return Number(this.query_stats.total) === Number(this.query_stats.resolved) ? 'sip-pill--good' : 'sip-pill--warn';
            },
            queryHealthLabel() {
                return Number(this.query_stats.total) === Number(this.query_stats.resolved)
                    ? this.trans('query.resolved')
                    : 'Open';
            },
            queryHealthTitle() {
                return Number(this.query_stats.total) === Number(this.query_stats.resolved)
                    ? 'All submitted queries are closed'
                    : 'Queries need operator attention';
            },
            queryHealthCopy() {
                const open = Math.max(0, Number(this.query_stats.total) - Number(this.query_stats.resolved));
                return `Total queries: ${this.query_stats.total} · Open, pending, escalated: ${open}.`;
            },
        },
        data() {
            return {
                instance_stats: emptyInstance(),
                query_stats: emptyQuery(),
                subscriber_stats: emptySubscriber(),
                graph: {
                    instance_status: {
                        labels: [],
                        datasets: []
                    }
                },
                activity_logs: [],
                todos: []
            }
        },
        filters: {
          momentDateTime(date) {
            return helper.formatDateTime(date);
          },
          moment(date) {
            return helper.formatDate(date);
          }
        },
    }
</script>
